/**
 * RTL (Right-to-Left) Support for Arabic Language
 * This script handles RTL-specific functionality and ensures proper RTL behavior
 */

(function () {
  'use strict';

  // Check if the current locale is Arabic
  function isArabicLocale() {
    return document.documentElement.lang === 'ar' ||
      document.documentElement.lang === 'ar-EG' ||
      document.documentElement.getAttribute('dir') === 'rtl';
  }

  // Initialize RTL support
  function initRTL() {
    if (!isArabicLocale()) {
      return;
    }

    // Add RTL class to body
    document.body.classList.add('rtl');

    // Handle form inputs
    handleFormInputs();

    // Handle navigation menus
    handleNavigationMenus();

    // Handle sliders and carousels
    handleSliders();

    // Handle modals and drawers
    handleModals();

    // Handle icons and arrows
    handleIcons();

    // Handle WhatsApp button position
    handleWhatsAppButton();

    // Handle quantity selectors
    handleQuantitySelectors();

    // Handle search functionality
    handleSearch();

    // Handle cart functionality
    handleCart();

    // Handle product variants
    handleProductVariants();

    // Handle pagination
    handlePagination();

    // Handle breadcrumbs
    handleBreadcrumbs();

    // Handle social media icons
    handleSocialMedia();

    // Handle newsletter forms
    handleNewsletter();

    // Handle tooltips
    handleTooltips();

    // Handle loading states
    handleLoadingStates();

    // Handle animations
    handleAnimations();
  }

  // Handle form inputs for RTL
  function handleFormInputs() {
    const inputs = document.querySelectorAll('input[type="text"], input[type="email"], input[type="password"], input[type="search"], textarea, select');

    inputs.forEach(input => {
      input.setAttribute('dir', 'rtl');
      input.style.textAlign = 'right';

      // Handle placeholder text direction
      if (input.placeholder) {
        input.setAttribute('data-placeholder', input.placeholder);
      }
    });
  }

  // Handle navigation menus for RTL
  function handleNavigationMenus() {
    const menus = document.querySelectorAll('.header__inline-menu, .list-menu');

    menus.forEach(menu => {
      menu.style.direction = 'rtl';

      // Handle submenu positioning
      const submenus = menu.querySelectorAll('.header__submenu, .disclosure__list-wrapper');
      submenus.forEach(submenu => {
        submenu.style.right = 'auto';
        submenu.style.left = '0';
      });
    });
  }

  // Handle sliders and carousels for RTL
  function handleSliders() {
    const sliders = document.querySelectorAll('.slider, .slideshow');

    sliders.forEach(slider => {
      // Prevent infinite loop by checking if already processed
      if (slider.getAttribute('data-rtl-processed') === 'true') {
        return;
      }

      // Skip product page slider as per request
      if (slider.classList.contains('product__media-list')) {
        slider.setAttribute('data-rtl-processed', 'true');
        return;
      }

      // Reverse slide order for RTL
      const slides = slider.querySelectorAll('.slider__slide, .slideshow__slide');
      if (slides.length > 0) {
        const container = slides[0].parentElement;
        const slidesArray = Array.from(slides);
        slidesArray.reverse().forEach(slide => {
          container.appendChild(slide);
        });
      }

      // Handle navigation buttons
      const prevButton = slider.querySelector('.slider-button--prev');
      const nextButton = slider.querySelector('.slider-button--next');

      if (prevButton && nextButton) {
        prevButton.style.left = 'auto';
        prevButton.style.right = '1rem';
        nextButton.style.right = 'auto';
        nextButton.style.left = '1rem';
      }

      // Mark as processed
      slider.setAttribute('data-rtl-processed', 'true');
    });
  }

  // Handle modals and drawers for RTL
  function handleModals() {
    const modals = document.querySelectorAll('.modal, .drawer');

    modals.forEach(modal => {
      modal.style.direction = 'rtl';

      // Handle close buttons
      const closeButton = modal.querySelector('.modal__close-button, .drawer__close-button');
      if (closeButton) {
        closeButton.style.right = 'auto';
        closeButton.style.left = '1rem';
      }
    });
  }

  // Handle icons and arrows for RTL
  function handleIcons() {
    const icons = document.querySelectorAll('.icon-arrow, .icon-caret, .icon-close');

    icons.forEach(icon => {
      icon.style.transform = 'scaleX(-1)';
    });
  }

  // Handle WhatsApp button position for RTL
  function handleWhatsAppButton() {
    const whatsappButton = document.querySelector('.whatsapp-float');
    if (whatsappButton) {
      whatsappButton.style.left = '1rem';
      whatsappButton.style.right = 'auto';
    }
  }

  // Handle quantity selectors for RTL
  function handleQuantitySelectors() {
    const quantitySelectors = document.querySelectorAll('.quantity');

    quantitySelectors.forEach(quantity => {
      const buttons = quantity.querySelectorAll('.quantity__button');
      if (buttons.length >= 2) {
        // Swap border radius for RTL
        buttons[0].style.borderRadius = '0 var(--buttons-radius) var(--buttons-radius) 0';
        buttons[buttons.length - 1].style.borderRadius = 'var(--buttons-radius) 0 0 var(--buttons-radius)';
      }
    });
  }

  // Handle search functionality for RTL
  function handleSearch() {
    const searchForms = document.querySelectorAll('.search-modal__form, .header__search');

    searchForms.forEach(form => {
      form.style.direction = 'rtl';

      const searchInput = form.querySelector('input[type="search"]');
      if (searchInput) {
        searchInput.style.textAlign = 'right';
        searchInput.setAttribute('dir', 'rtl');
      }
    });
  }

  // Handle cart functionality for RTL
  function handleCart() {
    const cartDrawer = document.querySelector('.cart-drawer');
    if (cartDrawer) {
      cartDrawer.style.direction = 'rtl';

      const cartItems = cartDrawer.querySelectorAll('.cart-item');
      cartItems.forEach(item => {
        item.style.direction = 'rtl';

        const itemDetails = item.querySelector('.cart-item__details');
        if (itemDetails) {
          itemDetails.style.textAlign = 'right';
        }
      });
    }
  }

  // Handle product variants for RTL
  function handleProductVariants() {
    const variantSelects = document.querySelectorAll('.variant-selects');

    variantSelects.forEach(select => {
      select.style.direction = 'rtl';

      const fields = select.querySelectorAll('.variant-selects__field');
      fields.forEach(field => {
        field.style.direction = 'rtl';

        const label = field.querySelector('.variant-selects__label');
        if (label) {
          label.style.textAlign = 'right';
        }
      });
    });
  }

  // Handle pagination for RTL
  function handlePagination() {
    const pagination = document.querySelector('.pagination');
    if (pagination) {
      pagination.style.direction = 'rtl';

      const paginationList = pagination.querySelector('.pagination__list');
      if (paginationList) {
        paginationList.style.direction = 'rtl';
      }
    }
  }

  // Handle breadcrumbs for RTL
  function handleBreadcrumbs() {
    const breadcrumbs = document.querySelector('.breadcrumb');
    if (breadcrumbs) {
      breadcrumbs.style.direction = 'rtl';

      const breadcrumbList = breadcrumbs.querySelector('.breadcrumb__list');
      if (breadcrumbList) {
        breadcrumbList.style.direction = 'rtl';
      }
    }
  }

  // Handle social media icons for RTL
  function handleSocialMedia() {
    const socialLists = document.querySelectorAll('.list-social');

    socialLists.forEach(list => {
      list.style.direction = 'rtl';
    });
  }

  // Handle newsletter forms for RTL
  function handleNewsletter() {
    const newsletterForms = document.querySelectorAll('.newsletter__form');

    newsletterForms.forEach(form => {
      form.style.direction = 'rtl';
    });
  }

  // Handle tooltips for RTL
  function handleTooltips() {
    const tooltips = document.querySelectorAll('.tooltip');

    tooltips.forEach(tooltip => {
      tooltip.style.direction = 'rtl';

      const content = tooltip.querySelector('.tooltip__content');
      if (content) {
        content.style.textAlign = 'right';
      }
    });
  }

  // Handle loading states for RTL
  function handleLoadingStates() {
    const loadingSpinners = document.querySelectorAll('.loading__spinner');

    loadingSpinners.forEach(spinner => {
      spinner.style.direction = 'rtl';
    });
  }

  // Handle animations for RTL
  function handleAnimations() {
    // Reverse slide animations for RTL
    const slideAnimations = document.querySelectorAll('.animate--slide-in');

    slideAnimations.forEach(animation => {
      if (animation.style.animationName === 'slideIn') {
        animation.style.animationDirection = 'reverse';
      }
    });
  }

  // Handle dynamic content loading
  function handleDynamicContent() {
    // Observe DOM changes for dynamically loaded content
    const observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach(function (node) {
            if (node.nodeType === 1) { // Element node
              // Re-apply RTL styles to new content
              if (node.querySelector) {
                handleFormInputs();
                handleNavigationMenus();
                handleSliders();
                handleModals();
                handleIcons();
                handleWhatsAppButton();
                handleQuantitySelectors();
                handleSearch();
                handleCart();
                handleProductVariants();
                handlePagination();
                handleBreadcrumbs();
                handleSocialMedia();
                handleNewsletter();
                handleTooltips();
                handleLoadingStates();
                handleAnimations();
              }
            }
          });
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // Handle window resize for RTL
  function handleResize() {
    if (isArabicLocale()) {
      // Re-apply RTL styles on resize
      setTimeout(() => {
        handleSliders();
        handleModals();
        handleWhatsAppButton();
      }, 100);
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initRTL);
  } else {
    initRTL();
  }

  // Handle dynamic content
  handleDynamicContent();

  // Handle window resize
  window.addEventListener('resize', handleResize);

  // Export functions for external use
  window.RTLSupport = {
    init: initRTL,
    isArabicLocale: isArabicLocale,
    handleFormInputs: handleFormInputs,
    handleNavigationMenus: handleNavigationMenus,
    handleSliders: handleSliders,
    handleModals: handleModals,
    handleIcons: handleIcons,
    handleWhatsAppButton: handleWhatsAppButton,
    handleQuantitySelectors: handleQuantitySelectors,
    handleSearch: handleSearch,
    handleCart: handleCart,
    handleProductVariants: handleProductVariants,
    handlePagination: handlePagination,
    handleBreadcrumbs: handleBreadcrumbs,
    handleSocialMedia: handleSocialMedia,
    handleNewsletter: handleNewsletter,
    handleTooltips: handleTooltips,
    handleLoadingStates: handleLoadingStates,
    handleAnimations: handleAnimations
  };

})(); 